/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Autocomplete as RootAutocomplete,
  TextField,
} from "@mui/material";
import MDBox from "dashboard-template/components/MDBox";
import MDTypography from "dashboard-template/components/MDTypography";
import { ErrorMessage } from "formik";
import { FC, forwardRef, SyntheticEvent } from "react";
import { AutoCompleteOption } from "types";

/**
 * Props for Autocomplete component with Formik integration.
 */
interface Props {
  options: AutoCompleteOption[];
  loading?: boolean;
  name?: string;
  label?: string;
  autoCompleteValue?: AutoCompleteOption;
  defaultValue?: AutoCompleteOption;
  value?: string;
  error?: boolean;
  formikOutside?: boolean;
  onChange?: (
    event: SyntheticEvent,
    value: AutoCompleteOption,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<AutoCompleteOption>
  ) => void;
  id?: string;
  noOptionsText?: string; 
}

/**
 * Custom autocomplete dropdown component with search and selection.
 * 
 * Features:
 * - Searchable dropdown with filtering
 * - Loading state support
 * - Formik integration with error handling
 * - Controlled and uncontrolled modes
 * - Custom option equality checking by ID
 * - Standard variant text field
 * - Error message display via Formik
 * 
 * @param options - Array of selectable options {id, label}
 * @param loading - Show loading indicator while fetching options
 * @param name - Field name for Formik integration
 * @param label - Input label text
 * @param autoCompleteValue - Controlled value
 * @param defaultValue - Initial default selection
 * @param onChange - Change handler callback
 * @returns Autocomplete dropdown component
 */
const Autocomplete: FC<Props> = forwardRef(
  (
    {
      options,
      loading,
      name,
      label,
      autoCompleteValue,
      value,
      onChange,
      defaultValue,
      formikOutside,
      error,
      id,
      noOptionsText 
    },
    ref
  ) => {
    return (
      <>
        <RootAutocomplete
          disablePortal
          options={options}
          loading={loading}
          value={autoCompleteValue}
          defaultValue={defaultValue}
          isOptionEqualToValue={(option, value) => {
            if (!value || !option) return false;
            return option.id === value.id;
          }}
          ref={ref}
          renderInput={(params) => (
            <TextField 
              {...params} 
              name={name} 
              variant="standard" 
              label={label} 
              value={value} 
              error={error}
            />
          )}
          onChange={onChange}
          id={id}
          noOptionsText={noOptionsText}
        />
        {!formikOutside && (
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              {(<ErrorMessage name={name} />) as any}
            </MDTypography>
          </MDBox>
        )}
      </>
    );
  }
);

export default Autocomplete;